import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

import { TrustResponseData, UserResponseData } from "@/interfaces/api";
import { Network, networksArray } from "@/interfaces/network";
import { NFTNetworkProps, NFTProps } from "@/interfaces/nft";
import { AccountsProps, NetworksData, Store, User } from "@/interfaces/store";
import {
  ApiVersion,
  EEnvironment,
  apiVersion,
  createObjectFromKeys,
  isNotNullOrUndefined,
  platformVersion,
  toMap,
} from "@/utils";
import { secToMilliseconds } from "@/utils/date";
import { isActionAllowed } from "@/utils/network";

type Actions = {
  updatePreload: (loading: boolean) => void;
  updateUser: (user: UserResponseData) => void;
  updateAccounts: (props: Partial<AccountsProps>) => void;
  switchNetwork: (network: Network) => void;
  updateScore: (network: Network, data: TrustResponseData) => void;
  readNews: () => void;
  eraseUserData: () => void;
  manualScoreUpdate: (network: Network, trustScore: number, expirationDate: number) => void;
  manualUserUpdate: (score: number, scoreExpiration: number, nftIds: NFTProps[], network?: Network) => void;
  updateUploadType: (type: UploadType) => void;
  updateUserLoading: (loading: boolean) => void;
};

export enum UploadType {
  Onchain = "onchain",
  Offchain = "offchain",
}

const defaultNetwork = Network.NEAR;

export const defaultUser = {
  news: null,
  nearAccount: null,
  evmAccount: null,
  tonAccount: null,
  solanaAccount: null,
  stellarAccount: null,
  top1000: null,
  rating: 0,
  isWaitingMint: false,
  achievements: [],
  networksData: createObjectFromKeys(networksArray, {} as NetworksData),
  telegram: false,
  x: false,
  totalScore: 0,
};

function getAccountId(network: Network, user: User) {
  switch (network) {
    case Network.NEAR: {
      return user.nearAccount;
    }
    case Network.SOLANA: {
      return user.solanaAccount;
    }
    case Network.TON: {
      return user.tonAccount;
    }
    case Network.STELLAR: {
      return user.stellarAccount;
    }
    default:
      return user.evmAccount;
  }
}

export const useStore = create<Store & Actions>()(
  immer((set) => ({
    preload: true,
    network: defaultNetwork,
    user: defaultUser,
    uploadType: UploadType.Offchain,
    userLoading: false,

    updateUploadType: (type: UploadType) => {
      set((state) => {
        state.uploadType = type;
      });
    },
    updateAccounts: (props) => {
      set((state) => {
        const { nearAccount, evmAccount, tonAccount, solanaAccount, stellarAccount } = props;
        if (nearAccount) state.user.nearAccount = nearAccount;
        if (evmAccount) state.user.evmAccount = evmAccount;
        if (tonAccount) state.user.tonAccount = tonAccount;
        if (solanaAccount) state.user.solanaAccount = solanaAccount;
        if (stellarAccount) state.user.stellarAccount = stellarAccount;
      });
    },

    updatePreload: (loading) => {
      set((state) => {
        state.preload = loading;
      });
    },

    updateUserLoading: (loading: boolean) => {
      set((state) => {
        state.userLoading = loading;
      });
    },

    updateUser: (user) => {
      set((state) => {
        state.user.isWaitingMint = user.isWaitingMint;
        state.user.news = user.news;
        state.user.telegram = user.telegram;
        state.user.x = user.x;
        state.user.achievements = user.achievements;
        state.user.totalScore = user.totalScore;

        const networksDataArr = user.scores
          .map(({ score, network, isMinted, expirationDate, top1000, rating }) => {
            if (!Object.values(Network).includes(network)) {
              return null;
            }

            const nft = user?.nfts?.[network] as NFTNetworkProps | undefined;
            const hasNFT = Boolean(nft?.array.length);

            const userLevel = isMinted ? (hasNFT || nft?.isPurchased ? (user.telegram && user.x ? 3 : 2) : 1) : 0;

            const newValue = state.user.networksData[network]?.retrievedScores?.trust || null;
            const availableAction = isActionAllowed(network, userLevel, score || 0, newValue, expirationDate);
            const retrievedScores = state.user.networksData[network]?.retrievedScores || undefined;

            return {
              network: network,
              level: userLevel,
              availableAction,
              accountId: getAccountId(network, state.user),
              retrievedScores,
              score: {
                value: score || 0,
                isMinted: isMinted,
                expirationDate: expirationDate,
                top1000,
                rating,
              },
              nft: nft || null,
            } as NetworksData;
          })
          .filter(isNotNullOrUndefined)
          .filter(({ network }) => {
            //TODO SHOULD BE TESTED
            if (platformVersion === EEnvironment.PRODUCTION) {
              return !network.includes("testnet");
            }
            return true;
          })
          .filter(({ network }) => {
            if (apiVersion === ApiVersion.v2) {
              return network.includes(Network.TON) || network.includes(Network.TON_TESTNET);
            }
            return true;
          })
          .sort((a) => (a.network === Network.TON ? -1 : 1));
        const networksDataObj = toMap(networksDataArr, "network");
        if (!networksDataObj[defaultNetwork]) {
          state.network = user.scores[0].network;
        }
        state.user.networksData = networksDataObj;
      });
    },

    manualUserUpdate: (score: number, scoreExpiration: number, nftIds: NFTProps[], network = Network.NEAR) => {
      set((state) => {
        const networksData = {
          network: network,
          level: 0,
          availableAction: false,
          score: {
            value: score || 0,
            isMinted: true,
            expirationDate: scoreExpiration,
            top1000: null,
            rating: null,
          },
          nft: { array: nftIds },
        } as unknown as NetworksData;

        const networksDataObj = toMap([networksData], "network");

        state.user.networksData = networksDataObj;
      });
    },

    switchNetwork: (network) =>
      set((state) => {
        state.network = network;
      }),

    updateScore: (network, retrievedScores) =>
      set((state) => {
        const prevNetworkData = state.user.networksData[network];
        const availableAction = isActionAllowed(
          network,
          prevNetworkData.level,
          prevNetworkData.score.value,
          retrievedScores.trust,
          secToMilliseconds(retrievedScores.expiration),
        );

        const updatedNetworkData: NetworksData = {
          ...prevNetworkData,
          availableAction,
          retrievedScores,
        };

        const updatedNetworksData = { ...state.user.networksData, [network]: { ...updatedNetworkData } };
        state.user.networksData = updatedNetworksData;
      }),

    manualScoreUpdate: (network, trustScore, expirationDate) =>
      set((state) => {
        const prevNetworkData = state.user.networksData[network];
        const availableAction = isActionAllowed(
          network,
          prevNetworkData.level,
          prevNetworkData.score.value,
          trustScore,
          expirationDate,
        );

        const nft = state.user.networksData[network].nft;
        const hasNFT = Boolean(nft?.array.length);

        const userLevel = hasNFT || nft?.isPurchased ? (state.user.telegram && state.user.x ? 3 : 2) : 1;

        const updatedNetworkData: NetworksData = {
          ...prevNetworkData,
          level: userLevel,
          availableAction,
          retrievedScores: undefined,
          score: {
            ...prevNetworkData.score,
            value: trustScore,
            expirationDate: expirationDate,
            isMinted: true,
          },
        };

        const updatedNetworksData = { ...state.user.networksData, [network]: { ...updatedNetworkData } };
        state.user.networksData = updatedNetworksData;
      }),

    readNews: () => {
      set((state) => {
        state.user.news = null;
      });
    },
    eraseUserData: () => {
      set((state) => {
        state.preload = true;
        state.user = defaultUser;
        state.network = defaultNetwork;
        localStorage.clear();
      });
    },
  })),
);
